import React, { useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import {
  useCreateFolderMutation,
  useDestroyFolderMutation,
  useUpdateFolderMutation,
} from 'components/hooks/folderMutations';
import { useCreateProjectsBulkArchiveMutation } from 'components/hooks/projectMutations';
import useRequestError from 'components/hooks/useRequestError';

import SuccessToast from 'components/application/SuccessToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import ArchiveProjectsModal from 'components/projectsDashboard/components/ArchiveProjectsModal';
import CreateFolderModal from 'components/projectsDashboard/components/CreateFolderModal';
import DestroyFolderModal from 'components/projectsDashboard/components/DestroyFolderModal';
import GroupIntoFolderModal from 'components/projectsDashboard/components/GroupIntoFolderModal';
import IndexSkeleton from 'components/projectsDashboard/components/IndexSkeleton';
import IndexWrapper from 'components/projectsDashboard/components/IndexWrapper';
import MoveToFolderModal from 'components/projectsDashboard/components/MoveToFolderModal';
import ProjectsDashboardSidebarMenu from 'components/projectsDashboard/components/ProjectsDashboardSidebarMenu';
import RenameFolderModal from 'components/projectsDashboard/components/RenameFolderModal';
import ProjectRelocationModalContextProvider from 'components/projectsDashboard/contexts/ProjectRelocationModalContext';

import { formatData } from 'components/helpers/reactQuery';
import { pluralizeProjectsName } from 'components/helpers/resources/projects';
import { asyncScrollToTop } from 'components/helpers/scrolling';

const sidebarMenuSections = ['archive', 'current', 'folders', 'recents'];

export default function ProjectsDashboard() {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'pages.ProjectsDashboardPage',
  });

  const { t: i18nTProjects } = useTranslation('common', {
    keyPrefix: 'projects',
  });

  const {
    defaultFolderId,
    folderPolicy,
    seedProjectsDashboardFilterSelectionConfiguration,
    seedProjectsDashboardTableConfiguration,
  } = useProjectsDashboardContext();

  const urlSearchParams = new URLSearchParams(window.location.search);
  const urlSectionSearchParam = urlSearchParams.get('section');

  const [activeSidebarMenuSection, setActiveSidebarMenuSection] = useState(
    defaultFolderId ? 'recents'
    : sidebarMenuSections.includes(urlSectionSearchParam) ?
      urlSectionSearchParam
    : 'current',
  );

  const [isArchiveProjectsModalOpen, setIsArchiveProjectsModalOpen] =
    useState(false);

  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);

  const [isDestroyFolderModalOpen, setIsDestroyFolderModalOpen] =
    useState(false);

  const [isGroupIntoFolderModalOpen, setIsGroupIntoFolderModalOpen] =
    useState(false);

  const [isMoveToFolderModalOpen, setIsMoveToFolderModalOpen] = useState(false);
  const [isRenameFolderModalOpen, setIsRenameFolderModalOpen] = useState(false);

  const [selectedFolderId, setSelectedFolderId] = useState(defaultFolderId);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);

  const indexWrapperRef = useRef(null);

  const [
    requestError,
    ,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const createProjectsBulkArchiveMutation =
    useCreateProjectsBulkArchiveMutation();

  const createFolderMutation = useCreateFolderMutation();
  const destroyFolderMutation = useDestroyFolderMutation();
  const updateFolderMutation = useUpdateFolderMutation();

  // window.performance is the most relaible way to detect browser navigation events
  const isReloadOrBackForwardBrowserNavigationEvent = [
    'reload',
    'back_forward',
  ].includes(window.performance.getEntriesByType('navigation')[0].type);

  // only able to reliably detect this browser navigation event from FE, so
  // doing what the BE would have done if this check had been done there instead
  const domainProjectsDashboardFilterSelectionConfigurationInitialData =
    isReloadOrBackForwardBrowserNavigationEvent ?
      { data: null, meta: { isBulkUpdateInProgress: true } }
    : seedProjectsDashboardFilterSelectionConfiguration;

  const { data: domainProjectsDashboardFilterSelectionConfiguration } =
    useQuery({
      queryKey: ['projects_dashboard_filter_selection_configuration'],
      queryFn: async () => {
        const response = await axios.get(
          '/projects_dashboards/filter_selection_configuration',
        );

        return response.data;
      },
      initialData:
        domainProjectsDashboardFilterSelectionConfigurationInitialData,
      enabled:
        domainProjectsDashboardFilterSelectionConfigurationInitialData.meta
          .isBulkUpdateInProgress,
      select: (data) =>
        formatData({
          data,
          resourceTypes: [
            'configurableFieldOptionFilterSelection',
            'optionFilterSelection',
            'dateFilterSelection',
            'userFilterSelection',
            'folderFilterSelection',
          ],
        }),
      refetchInterval: (data) =>
        !data || data.meta.isBulkUpdateInProgress ? 1000 : true,
      refetchIntervalInBackground: true,
    });

  // only able to reliably detect this browser navigation event from FE, so
  // doing what the BE would have done if this check had been done there instead
  const domainProjectsDashboardTableConfigurationInitialData =
    isReloadOrBackForwardBrowserNavigationEvent ?
      { data: null, meta: { isBulkUpdateInProgress: true } }
    : seedProjectsDashboardTableConfiguration;

  const { data: domainProjectsDashboardTableConfiguration } = useQuery({
    queryKey: ['projects_dashboard_table_configuration'],
    queryFn: async () => {
      const response = await axios.get(
        '/projects_dashboards/table_configuration',
      );

      return response.data;
    },
    initialData: domainProjectsDashboardTableConfigurationInitialData,
    enabled:
      domainProjectsDashboardTableConfigurationInitialData.meta
        .isBulkUpdateInProgress,
    select: (data) =>
      formatData({
        data,
        resourceTypes: ['tableColumnConfiguration', 'tableSortConfiguration'],
      }),
    refetchInterval: (data) =>
      !data || data.meta.isBulkUpdateInProgress ? 1000 : false,
    refetchIntervalInBackground: true,
  });

  const {
    data: domainFoldersMetaOnly,
    isSuccess: isDomainFoldersFetchSuccess,
  } = useQuery({
    enabled: folderPolicy.canIndex,
    queryKey: ['folders', 'totalCount'],
    queryFn: async () => {
      const response = await axios.post('/folder_searches', {
        folder_search: { meta_only: true },
      });

      return response.data;
    },
    keepPreviousData: true,
  });

  const handleFolderClick = ({ folderId, section }) => {
    asyncScrollToTop({ behavior: 'instant', ref: indexWrapperRef }).then(() => {
      setActiveSidebarMenuSection(section);
      setSelectedFolderId(folderId);

      if (folderId !== selectedFolderId) setSelectedProjectIds([]);
    });
  };

  const handleSidebarMenuSectionClick = ({ section }) => {
    if (section === activeSidebarMenuSection) return;

    asyncScrollToTop({ behavior: 'instant', ref: indexWrapperRef }).then(() => {
      setActiveSidebarMenuSection(section);
      setSelectedFolderId(null);
      setSelectedProjectIds([]);
    });
  };

  const handleCreateFolderSubmit = ({ folderParams }) => {
    createFolderMutation.mutate(
      { folderParams },
      {
        onSuccess: (data) => {
          handleCreateFolderSuccess({ folderId: data.data.id });
          resetRequestError();
          setIsCreateFolderModalOpen(false);
        },
        onError: (error) => {
          if (error.response.status === 422) {
            handleRequestError(error);
          } else {
            breadBoard.addInedibleToast({
              fullMessage: i18nTProjects('errors.folderCreation'),
            });
          }
        },
      },
    );
  };

  const handleCreateFolderSuccess = ({ folderId }) => {
    breadBoard.addToast(
      <SuccessToast
        message={i18nT('handleCreateFolderSuccess.message')}
        onBurnToast={breadBoard.handleBurnToast}
      />,
    );

    setActiveSidebarMenuSection('recents');
    setSelectedFolderId(folderId);
    setSelectedProjectIds([]);
  };

  const handleCreateFolderModalClose = () => {
    resetRequestError();
    setIsCreateFolderModalOpen(false);
  };

  const handleRenameFolderClick = ({ id }) => {
    setIsRenameFolderModalOpen(true);
    setSelectedFolderId(id);
  };

  const handleRenameFolderSubmit = ({ folderParams, id }) => {
    updateFolderMutation.mutate(
      { folderParams, id },
      {
        onSuccess: (data) => {
          breadBoard.addToast(
            <SuccessToast
              message={
                <Trans
                  i18nKey='pages.ProjectsDashboardPage.handleRenameFolderSubmit.success'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{ name: data.data.attributes.name }}
                />
              }
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );

          handleRenameFolderModalClose();
        },
        onError: handleRequestError,
      },
    );
  };

  const handleRenameFolderModalClose = () => {
    resetRequestError();
    setIsRenameFolderModalOpen(false);
    setSelectedFolderId(null);
  };

  const handleDeleteFolderClick = ({ id }) => {
    setIsDestroyFolderModalOpen(true);
    setSelectedFolderId(id);
  };

  const handleDestroyFolder = ({ id, name, params, projectCount }) => {
    destroyFolderMutation.mutate(
      { id, params },
      {
        onSuccess: () => {
          const ramsName = pluralizeProjectsName({
            count: projectCount,
            currentActor,
          });

          breadBoard.addToast(
            <SuccessToast
              message={
                params.projects_action ?
                  params.projects_action === 'archive' ?
                    i18nTProjects('archivedWithCount', {
                      count: projectCount,
                      interpolation: { escapeValue: false },
                      ramsName,
                    })
                  : i18nT('handleDestroyFolder.division', {
                      count: projectCount,
                      interpolation: { escapeValue: false },
                      ramsName,
                    })

                : i18nT('handleDestroyFolder.success', {
                    interpolation: { escapeValue: false },
                    name,
                  })
              }
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );

          asyncScrollToTop({ behavior: 'instant', ref: indexWrapperRef }).then(
            () => {
              setIsDestroyFolderModalOpen(false);
              setSelectedFolderId(null);

              if (activeSidebarMenuSection !== 'folders') {
                setActiveSidebarMenuSection(
                  params.projects_action === 'archive' ? 'archive' : 'current',
                );
              }
            },
          );
        },
        onError: () => {
          breadBoard.addInedibleToast({
            fullMessage: i18nT('handleDestroyFolder.error', {
              interpolation: { escapeValue: false },
              name,
            }),
          });
        },
      },
    );
  };

  const handleDestroyFolderModalClose = () => {
    if (activeSidebarMenuSection === 'folders') setSelectedFolderId(null);

    setIsDestroyFolderModalOpen(false);
  };

  const handleProjectToggle = ({ id }) => {
    setSelectedProjectIds((prevState) => {
      if (selectedProjectIds.includes(id)) {
        return prevState.filter(
          (selectedProjectId) => selectedProjectId !== id,
        );
      } else {
        return prevState.concat([id]);
      }
    });
  };

  const handleSelectAllProjectsToggle = ({ projectIds }) => {
    setSelectedProjectIds((prevState) =>
      prevState.length > 0 ? [] : projectIds,
    );
  };

  const handleMoveToFolderSuccess = ({ folder, selectedProjectIdsCount }) => {
    const ramsName = pluralizeProjectsName({
      count: selectedProjectIdsCount,
      currentActor,
    });

    breadBoard.addToast(
      <SuccessToast
        message={
          folder ?
            <Trans
              components={{
                button: (
                  <button
                    className='tw-max-w-[100px] tw-truncate tw-border-0 tw-bg-transparent tw-p-0 tw-font-medium tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                    onClick={() => {
                      handleFolderClick({
                        folderId: folder.id,
                        section: 'recents',
                      });
                    }}
                    type='button'
                  />
                ),
              }}
              i18nKey='pages.ProjectsDashboardPage.handleMoveToFolderSuccess.inFolder.success'
              ns='components'
              tOptions={{ interpolation: { escapeValue: false } }}
              values={{
                count: selectedProjectIdsCount,
                folderName: folder.attributes.name,
                ramsName,
              }}
            />
          : i18nT('handleMoveToFolderSuccess.outOfFolder.success', {
              count: selectedProjectIdsCount,
              interpolation: { escapeValue: false },
              ramsName,
            })
        }
        onBurnToast={breadBoard.handleBurnToast}
      />,
    );

    setIsMoveToFolderModalOpen(false);
    setSelectedProjectIds([]);
  };

  const handleArchiveProjectsSubmit = () => {
    createProjectsBulkArchiveMutation.mutate(
      { bulkArchiveParams: { project_ids: selectedProjectIds } },
      {
        onSuccess: () => {
          const count = selectedProjectIds.length;

          breadBoard.addToast(
            <SuccessToast
              message={i18nTProjects('archivedWithCount', {
                count,
                interpolation: { escapeValue: false },
                ramsName: pluralizeProjectsName({
                  count,
                  currentActor,
                }),
              })}
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );

          setIsArchiveProjectsModalOpen(false);
          setSelectedProjectIds([]);
        },
        onError: () => {
          breadBoard.addInedibleToast({
            fullMessage: i18nT('handleArchiveProjectsSubmit.error', {
              interpolation: { escapeValue: false },
              ramsName: pluralizeProjectsName({
                count: selectedProjectIds.length,
                currentActor,
              }),
            }),
          });

          setIsArchiveProjectsModalOpen(false);
        },
      },
    );
  };

  const hasFolders =
    isDomainFoldersFetchSuccess && domainFoldersMetaOnly.meta.totalCount > 0;

  const areAnyConfigurationBulkUpdatesInProgress =
    domainProjectsDashboardFilterSelectionConfiguration?.meta
      .isBulkUpdateInProgress ||
    domainProjectsDashboardTableConfiguration?.meta.isBulkUpdateInProgress;

  const urlCurrentSearchParam = urlSearchParams.get('search');

  (function deleteSearchParamsIfPresent() {
    const url = new URL(window.location.href);

    if (
      url.searchParams.has('folder_id') ||
      url.searchParams.has('search') ||
      url.searchParams.has('section')
    ) {
      url.searchParams.delete('folder_id');
      url.searchParams.delete('search');
      url.searchParams.delete('section');

      window.history.replaceState(null, '', url.href);
    }
  })();

  return (
    <>
      <div className='tw-flex tw-h-[calc(100vh-160px)]'>
        <div className='tw-w-54'>
          <ProjectsDashboardSidebarMenu
            activeSection={activeSidebarMenuSection}
            hasFolders={hasFolders}
            onFolderClick={handleFolderClick}
            onNewFolderClick={() => setIsCreateFolderModalOpen(true)}
            onSectionClick={handleSidebarMenuSectionClick}
            selectedFolderId={selectedFolderId}
          />
        </div>
        {areAnyConfigurationBulkUpdatesInProgress ?
          <IndexSkeleton />
        : <IndexWrapper
            activeSidebarMenuSection={activeSidebarMenuSection}
            defaultCurrentSearch={urlCurrentSearchParam}
            domainProjectsDashboardFilterSelectionConfiguration={
              domainProjectsDashboardFilterSelectionConfiguration
            }
            domainProjectsDashboardTableConfiguration={
              domainProjectsDashboardTableConfiguration
            }
            hasFolders={hasFolders}
            indexWrapperRef={indexWrapperRef}
            onArchiveProjectsClick={() => setIsArchiveProjectsModalOpen(true)}
            onDebouncedFiltersChange={() => setSelectedProjectIds([])}
            onDeleteFolderClick={handleDeleteFolderClick}
            onFolderClick={handleFolderClick}
            onGroupIntoFolderClick={() => setIsGroupIntoFolderModalOpen(true)}
            onMoveToFolderClick={() => setIsMoveToFolderModalOpen(true)}
            onNewFolderClick={() => setIsCreateFolderModalOpen(true)}
            onProjectArchiveClose={() => setSelectedProjectIds([])}
            onProjectDestroyClose={() => setSelectedProjectIds([])}
            onProjectToggle={handleProjectToggle}
            onProjectsPageChange={() => setSelectedProjectIds([])}
            onRenameFolderClick={handleRenameFolderClick}
            onSelectAllProjectsToggle={handleSelectAllProjectsToggle}
            onSidebarMenuSectionClick={handleSidebarMenuSectionClick}
            selectedFolderId={selectedFolderId}
            selectedProjectIds={selectedProjectIds}
          />
        }
      </div>
      {folderPolicy.canIndex && (
        <>
          {isCreateFolderModalOpen && (
            <CreateFolderModal
              isCreateDisabled={createFolderMutation.isLoading}
              onClose={handleCreateFolderModalClose}
              onCreateSubmit={handleCreateFolderSubmit}
              onInputFocus={removeErrorStyling}
              requestError={requestError}
            />
          )}
          {isDestroyFolderModalOpen && !!selectedFolderId && (
            <DestroyFolderModal
              folderId={selectedFolderId}
              isDestroyDisabled={destroyFolderMutation.isLoading}
              onClose={handleDestroyFolderModalClose}
              onDestroy={handleDestroyFolder}
            />
          )}
          <ProjectRelocationModalContextProvider>
            {isGroupIntoFolderModalOpen && selectedProjectIds.length > 0 && (
              <GroupIntoFolderModal
                onClose={() => setIsGroupIntoFolderModalOpen(false)}
                onMoveToExistingFolderClick={() => {
                  setIsGroupIntoFolderModalOpen(false);
                  setIsMoveToFolderModalOpen(true);
                }}
                onSuccess={({ folderId }) => {
                  handleCreateFolderSuccess({ folderId });
                  setIsGroupIntoFolderModalOpen(false);
                }}
                selectedProjectIds={selectedProjectIds}
              />
            )}
            {isMoveToFolderModalOpen && selectedProjectIds.length > 0 && (
              <MoveToFolderModal
                contextualFolderId={selectedFolderId}
                onClose={() => setIsMoveToFolderModalOpen(false)}
                onGroupIntoFolderClick={() => {
                  setIsGroupIntoFolderModalOpen(true);
                  setIsMoveToFolderModalOpen(false);
                }}
                onSuccess={handleMoveToFolderSuccess}
                selectedProjectIds={selectedProjectIds}
              />
            )}
          </ProjectRelocationModalContextProvider>
          {isRenameFolderModalOpen && !!selectedFolderId && (
            <RenameFolderModal
              folderId={selectedFolderId}
              isConfirmDisabled={updateFolderMutation.isLoading}
              key={selectedFolderId}
              onClose={handleRenameFolderModalClose}
              onConfirm={handleRenameFolderSubmit}
              onInputFocus={removeErrorStyling}
              requestError={requestError}
            />
          )}
        </>
      )}
      {isArchiveProjectsModalOpen && selectedProjectIds.length > 0 && (
        <ArchiveProjectsModal
          isArchiveProjectsDisabled={
            createProjectsBulkArchiveMutation.isLoading
          }
          onArchiveProjectsSubmit={handleArchiveProjectsSubmit}
          onClose={() => setIsArchiveProjectsModalOpen(false)}
          projectsCount={selectedProjectIds.length}
        />
      )}
    </>
  );
}
